<template>
  <div class="groups-page eh-ds">
    <div class="groups-header">
      <div class="groups-header-top">
        <h1>
          {{
            isSelfViewOnly
              ? "My Privileges"
              : organizationDisplayName + ": Privileges"
          }}
        </h1>
        <help-icon help-id="privileges" title="Help for Privileges" />
      </div>
      <p v-if="isSelfViewOnly" class="self-view-banner">
        Read-only view of your own privileges. You cannot change grants or view other users.
      </p>
    </div>

    <div class="groups-body">
      <div class="privileges-grid">
        <div
          v-if="!isSelfViewOnly"
          class="privileges-layout-toggle"
          role="radiogroup"
          aria-label="Primary column"
        >
          <label class="privileges-layout-toggle-option">
            <input type="radio" value="groups_first" v-model="layoutMode" />
            <span>Groups/Users</span>
          </label>
          <label class="privileges-layout-toggle-option">
            <input type="radio" value="privileges_first" v-model="layoutMode" />
            <span>Privileges</span>
          </label>
        </div>

        <div class="privileges-main-row" :class="{ 'privileges-main-row--self': isSelfViewOnly }">
          <div
            v-if="!isSelfViewOnly"
            class="tree-panel-slot"
            :style="{ order: layoutMode === 'groups_first' ? 1 : 2 }"
          >
            <div v-if="showGroupsColumnPayload" class="groups-panel groups-panel--tree">
              <div class="tree-panel-row tree-panel-row--title">
                <div class="list-title">user / group</div>
                <span v-if="showGroupGreenPrompt" class="selection-prompt">
                  please select a group from the list below
                </span>
              </div>
              <div class="tree-panel-row tree-panel-row--actions">
                <button class="btn btn-secondary" type="button" @click="goUpLevel" :disabled="!canGoUpLevel">
                  back up a level
                </button>
              </div>

              <Tree
                v-model:expandedKeys="expandedTreeKeys"
                v-model:selectionKeys="selectedTreeKeys"
                :value="groupTreeNodes"
                selectionMode="single"
                class="groups-tree"
                @nodeSelect="onTreeNodeSelect"
                @node-expand="onTreeNodeExpand"
              >
                <template #default="slotProps">
                  <div
                    class="tree-node-label"
                    :class="{
                      'tree-node-label--group': isTreeGroupNode(slotProps.node),
                      'tree-node-label--person': isTreePersonNode(slotProps.node),
                    }"
                    @click.stop="onTreeRowClick(slotProps.node)"
                  >
                    <i v-if="isTreePersonNode(slotProps.node)" class="pi pi-user user-silhouette-icon" aria-hidden="true"></i>
                    <span class="tree-node-label-text">{{ slotProps.node.label }}</span>
                  </div>
                </template>
              </Tree>
            </div>
            <div v-else class="groups-panel groups-panel--tree groups-panel--placeholder" aria-hidden="true">
              <div class="tree-panel-row tree-panel-row--title tree-panel-row--placeholder" />
              <div class="tree-panel-row tree-panel-row--actions tree-panel-row--placeholder" />
            </div>
          </div>

          <div
            class="tree-panel-slot"
            :style="{ order: isSelfViewOnly ? 1 : layoutMode === 'groups_first' ? 2 : 1 }"
          >
            <div v-if="showPrivilegeColumn" class="groups-panel groups-panel--tree">
              <div class="tree-panel-row tree-panel-row--title">
                <div class="list-title">
                  {{ isSelfViewOnly ? selfDisplayName + " — privileges" : "privileges" }}
                </div>
                <div v-if="showPrivilegeGreenPrompt" class="privilege-select-prompt-row">
                  <span class="privilege-select-prompt">please select a privilege from the list below</span>
                </div>
              </div>
              <div class="tree-panel-row tree-panel-row--actions tree-panel-row--actions-stack">
                <div class="privileges-search-field">
                  <input
                    v-model="privilegeSearchQuery"
                    type="search"
                    placeholder="search privileges"
                    aria-label="Search privileges"
                  />
                </div>
                <div class="privileges-filter-row">
                  <label><input type="radio" value="all" v-model="privilegeVisibilityFilter" /> show all</label>
                  <label><input type="radio" value="granted" v-model="privilegeVisibilityFilter" /> only granted</label>
                  <label><input type="radio" value="not_granted" v-model="privilegeVisibilityFilter" /> not granted</label>
                  <label v-if="isUserSubject">
                    <input type="radio" value="denied" v-model="privilegeVisibilityFilter" /> only denied
                  </label>
                </div>
              </div>

              <Tree
                v-model:expandedKeys="expandedPrivilegeKeys"
                v-model:selectionKeys="selectedPrivilegeKeys"
                :value="privilegeTreeNodes"
                selectionMode="single"
                class="groups-tree"
                @nodeSelect="onPrivilegeNodeSelect"
              >
                <template #default="slotProps">
                  <!-- differing combined classes for group, privilege, granted, denied, not granted -->
                  <div
                    class="tree-node-label"
                    :class="{
                      'tree-node-label--group': slotProps.node?.data?.type === 'category',
                      'tree-node-label--person': slotProps.node?.data?.type === 'privilege',
                      'tree-node-label--granted':
                        slotProps.node?.data?.type === 'privilege' &&
                        slotProps.node?.data?.privilege?.effective_granted === true &&
                        slotProps.node?.data?.privilege?.user_override !== 'deny',
                      'tree-node-label--denied':
                        slotProps.node?.data?.type === 'privilege' &&
                        slotProps.node?.data?.privilege?.user_override === 'deny',
                      'tree-node-label--not-granted':
                        slotProps.node?.data?.type === 'privilege' &&
                        slotProps.node?.data?.privilege?.effective_granted !== true &&
                        slotProps.node?.data?.privilege?.user_override !== 'deny',
                    }"
                    @click.stop="onPrivilegeRowClick(slotProps.node)"
                  >
                    <span class="tree-node-label-text">{{ slotProps.node.label }}</span>
                  </div>
                </template>
              </Tree>
            </div>
            <div v-else class="groups-panel privilege-detail-panel" aria-hidden="true" />
          </div>

          <div class="tree-panel-slot tree-panel-slot--access" :style="{ order: 3 }">
            <div class="groups-panel privilege-detail-panel">
          <template v-if="selectedPrivilege && hasSubjectSelection">
            <div class="privilege-detail-body">
              <div class="list-title">selected privilege:</div>
              <div class="privilege-detail-name">"{{ selectedPrivilege.name || selectedPrivilege.label }}"</div>
              <!-- Self view: status only, no grant/deny radios or Save. -->
              <div v-if="isSelfViewOnly" class="privilege-readonly-status" aria-label="Privilege status">
                <span
                  class="privilege-readonly-pill"
                  :class="{
                    'privilege-readonly-pill--granted': selfPrivilegeStatusKey === 'granted',
                    'privilege-readonly-pill--denied': selfPrivilegeStatusKey === 'denied',
                    'privilege-readonly-pill--not-granted': selfPrivilegeStatusKey === 'not_granted',
                  }"
                >{{ selfPrivilegeStatusLabel }}</span>
              </div>
              <div v-else-if="!isUserSubject" class="privilege-grant-radios" role="radiogroup" aria-label="Group grant status">
                <label class="privilege-grant-option privilege-granted">
                  <input type="radio" value="granted" v-model="privilegeGrantChoice" />
                  <span>Grant here</span>
                </label>
                <label class="privilege-grant-option privilege-not-granted">
                  <input type="radio" value="not_granted" v-model="privilegeGrantChoice" />
                  <span>Not granted</span>
                </label>
              </div>
              <div v-else class="privilege-grant-radios" role="radiogroup" aria-label="User privilege override">
                <label class="privilege-grant-option privilege-granted">
                  <input type="radio" value="grant" v-model="privilegeGrantChoice" />
                  <span>Grant here</span>
                </label>
                <label class="privilege-grant-option privilege-not-granted">
                  <input type="radio" value="not_granted" v-model="privilegeGrantChoice" />
                  <span>Not granted</span>
                </label>
                <label class="privilege-grant-option privilege-deny">
                  <input type="radio" value="deny" v-model="privilegeGrantChoice" />
                  <span>Deny</span>
                </label>
              </div>
              <div v-if="showPrivilegeInheritanceNote" class="privilege-inheritance-note">
                <p class="privilege-inheritance-note-lead">{{ privilegeInheritanceLead }}</p>
                <ul class="privilege-inheritance-note-list">
                  <li v-for="g in privilegeInheritedGroups" :key="g.group_id || g.name">{{ g.name || g.group_id }}</li>
                </ul>
                <p v-if="showAlsoGrantedIndividuallyLine" class="privilege-inheritance-note-extra">
                  And also granted individually.
                </p>
                <p v-if="showUserDenyVsGroupsLine" class="privilege-inheritance-note-deny">
                  {{ privilegeInheritanceDenyLine }}
                </p>
              </div>
            </div>
            <div v-if="!isSelfViewOnly" class="privilege-detail-actions">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="!privilegeGrantDirty || savingPrivilegeGrant || loadingPrivilegeAssignments"
                @click="savePrivilegeGrant"
              >
                Save
              </button>
              <button type="button" class="btn btn-secondary" @click="quitPrivilegePanel">Quit</button>
            </div>
          </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import { mapState } from "pinia";
import { CURR_API } from "@/constants/apis";
import { syncPrivilegesAfterSessionChange } from "@/utils/privilegesSessionSync";
import HelpIcon from "@/components/help/HelpIcon.vue";
import { useOrganizationStore } from "@/stores/organizationStore";
import { usePrivilegesStore } from "@/stores/privileges";
import { PRIVILEGES } from "@/constants/privileges";
import { useSessionStore } from "@/stores/session";

export default {
  name: "PrivilegesPage",
  components: {
    HelpIcon,
  },
  data() {
    return {
      groupsAll: [],
      loadingGroups: false,
      selectedGroupId: null,
      selectedPatientUniqueId: null,
      selectedTreeKeys: {},
      expandedTreeKeys: {},
      patientsMembers: [],
      loadedGlobalGroupMembers: {},
      loadedOwnerGroups: {},
      accountUsers: [],
      selectedPersonGlobalGroupId: null,
      privilegesAll: [],
      privilegesCatalog: [],
      loadingPrivileges: false,
      loadingPrivilegeAssignments: false,
      expandedPrivilegeKeys: {},
      selectedPrivilegeKeys: {},
      privilegeVisibilityFilter: "all",
      privilegeSearchQuery: "",
      selectedPrivilege: null,
      privilegeGrantChoice: "not_granted",
      privilegeGrantBaseline: "not_granted",
      savingPrivilegeGrant: false,
      layoutMode: "groups_first",
    };
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    // The signed-in person, from the token-backed session (`GET /me`,
    // decision D4) and nothing else: which view this page opens in — the
    // editor or the read-only self view — and whose privileges the self view
    // shows depend on it. mapState so both follow `/me` when it answers.
    ...mapState(useSessionStore, {
      sessionUserId: "userId",
      sessionDisplayName: "displayName",
      sessionOrganizationNumber: "organizationNumber",
    }),
    /** Full editor requires adjust_permissions; otherwise this page is self read-only. */
    canManagePrivileges() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.ADJUST_PERMISSIONS);
    },
    isSelfViewOnly() {
      return !this.canManagePrivileges;
    },
    /**
     * The signed-in user's own id, from the token-backed session only; ''
     * until `/me` has answered (mounted waits for it). Never derived from
     * `allData`, whose `patient` is the open chart.
     */
    selfUniqueId() {
      const id = this.sessionUserId;
      return id === null || id === undefined ? "" : String(id);
    },
    selfDisplayName() {
      return this.sessionDisplayName || "You";
    },
    // key for the self privilege status (granted, denied, not_granted)
    selfPrivilegeStatusKey() {
      const p = this.selectedPrivilege;
      if (!p) return "not_granted";
      if (p.user_override === "deny") return "denied";
      if (p.effective_granted === true) return "granted";
      return "not_granted";
    },
    // label for the self privilege status (Granted, Denied, Not granted)
    selfPrivilegeStatusLabel() {
      if (this.selfPrivilegeStatusKey === "denied") return "Denied";
      if (this.selfPrivilegeStatusKey === "granted") return "Granted";
      return "Not granted";
    },
    accountNumber() {
      const number = this.sessionOrganizationNumber;
      return number === null || number === undefined ? "" : String(number);
    },
    organizationDisplayName() {
      return useOrganizationStore().organizationName || "Organization";
    },
    hasSubjectSelection() {
      if (this.isSelfViewOnly) {
        const uid = this.selectedPatientUniqueId != null ? String(this.selectedPatientUniqueId).trim() : "";
        return uid.length > 0;
      }
      if (this.selectedGroupId != null && String(this.selectedGroupId).trim() !== "" && Number(this.selectedGroupId) > 0) {
        return true;
      }
      const uid = this.selectedPatientUniqueId != null ? String(this.selectedPatientUniqueId).trim() : "";
      return uid.length > 0;
    },
    isUserSubject() {
      const uid = this.selectedPatientUniqueId != null ? String(this.selectedPatientUniqueId).trim() : "";
      return uid.length > 0 && !this.selectedGroupId;
    },
    subjectPrivilegeKey() {
      if (!this.hasSubjectSelection) return null;
      if (this.selectedGroupId != null && Number(this.selectedGroupId) > 0) {
        return `g:${this.selectedGroupId}`;
      }
      const uid = this.selectedPatientUniqueId != null ? String(this.selectedPatientUniqueId).trim() : "";
      return uid ? `u:${uid}` : null;
    },
    showGroupsColumnPayload() {
      if (this.isSelfViewOnly) return false;
      return this.layoutMode === "groups_first" || !!this.selectedPrivilege;
    },
    showPrivilegeColumn() {
      if (this.isSelfViewOnly) return true;
      return this.layoutMode === "privileges_first" || this.hasSubjectSelection;
    },
    showGroupGreenPrompt() {
      if (this.isSelfViewOnly) return false;
      if (this.layoutMode === "groups_first") return !this.hasSubjectSelection;
      return !!(this.selectedPrivilege && !this.hasSubjectSelection);
    },
    showPrivilegeGreenPrompt() {
      if (this.isSelfViewOnly) return !this.selectedPrivilege;
      if (this.layoutMode === "privileges_first") return !this.selectedPrivilege;
      return this.hasSubjectSelection && !this.selectedPrivilege;
    },
    privilegeInheritedGroups() {
      const p = this.selectedPrivilege;
      if (!p || !Array.isArray(p.inherited_from_groups)) return [];
      return p.inherited_from_groups;
    },
    showPrivilegeInheritanceNote() {
      return this.privilegeInheritedGroups.length > 0;
    },
    // Port: the list also names roles and an organization-wide emergency
    // incident (privilegesBridge, `kind` on each entry). Main's wording, "the
    // following group(s) this user is in", is kept for a list of groups only;
    // anything else is introduced as what it is.
    privilegeInheritedOnlyGroups() {
      return this.privilegeInheritedGroups.every((g) => !g || !g.kind || g.kind === "group");
    },
    privilegeInheritanceLead() {
      if (this.privilegeInheritedOnlyGroups) {
        return "Note: this privilege is implicitly granted by the following group(s) this user is in:";
      }
      const kinds = new Set(this.privilegeInheritedGroups.map((g) => (g && g.kind) || "group"));
      const parts = [];
      if (kinds.has("group")) parts.push("group(s) this user is in");
      if (kinds.has("role")) parts.push("role(s) this user holds");
      if (kinds.has("break_glass")) parts.push("emergency (break-glass) incident covering this user");
      const named = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : parts[0];
      return `Note: this privilege is implicitly granted by the following ${named}:`;
    },
    privilegeInheritanceDenyLine() {
      return this.privilegeInheritedOnlyGroups
        ? "Explicit deny is set for this user, group grants listed above do not apply."
        : "Explicit deny is set for this user, the grants listed above do not apply.";
    },
    showAlsoGrantedIndividuallyLine() {
      return (
        this.isUserSubject &&
        this.selectedPrivilege &&
        this.selectedPrivilege.user_override === "grant" &&
        this.privilegeInheritedGroups.length > 0
      );
    },
    showUserDenyVsGroupsLine() {
      return (
        this.isUserSubject &&
        this.selectedPrivilege &&
        this.selectedPrivilege.user_override === "deny" &&
        this.privilegeInheritedGroups.length > 0
      );
    },
    patientsGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          String(g.name || "")
            .trim()
            .toLowerCase() === "patients" &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },
    nursesGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          ["nurses", "nurse"].includes(
            String(g.name || "")
              .trim()
              .toLowerCase()
          ) &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },
    providersGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          ["providers", "provider"].includes(
            String(g.name || "")
              .trim()
              .toLowerCase()
          ) &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },
    homeHealthAidsGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          ["home health aid", "home health aids", "home health aide", "home health aides"].includes(
            String(g.name || "")
              .trim()
              .toLowerCase()
          ) &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },
    alliedProfessionalsGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          ["allied professionals", "allied professional"].includes(
            String(g.name || "")
              .trim()
              .toLowerCase()
          ) &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },
    // Org-level GCM group id for privilege tree member injection (mirrors Groups page).
    geriatricCareManagersGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          ["geriatric care managers", "geriatric care manager"].includes(
            String(g.name || "")
              .trim()
              .toLowerCase()
          ) &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },
    memberInjectableGroupIds() {
      return new Set(
        [
          this.patientsGroupId,
          this.nursesGroupId,
          this.providersGroupId,
          this.homeHealthAidsGroupId,
          this.alliedProfessionalsGroupId,
          this.geriatricCareManagersGroupId,
        ].filter(Boolean)
      );
    },
    ownerGroupsFlat() {
      return Object.values(this.loadedOwnerGroups || {}).flat();
    },
    groupsById() {
      const out = {};
      for (const g of [...(this.groupsAll || []), ...this.ownerGroupsFlat]) {
        out[Number(g.id)] = g;
        out[String(g.id)] = g;
      }
      return out;
    },
    selectedGroup() {
      if (!this.selectedGroupId) return null;
      return this.groupsById[this.selectedGroupId] || this.groupsById[String(this.selectedGroupId)] || null;
    },
    parentOfSelected() {
      if (!this.selectedGroup) return null;
      if (!this.selectedGroup.parent_group_id) return null;
      return this.groupsById[Number(this.selectedGroup.parent_group_id)] || null;
    },
    canGoUpLevel() {
      if (this.selectedPatientUniqueId && !this.selectedGroupId) {
        const uid = this.selectedPatientUniqueId;
        const ggid =
          this.selectedPersonGlobalGroupId != null && Number(this.selectedPersonGlobalGroupId) > 0
            ? Number(this.selectedPersonGlobalGroupId)
            : this.globalGroupIdForUid(uid);
        return ggid != null && Number(ggid) > 0;
      }
      if (!this.selectedGroupId) return false;
      const g = this.selectedGroup;
      if (!g) return false;
      const pp = g.parent_group_id;
      const hasParent = pp != null && String(pp).trim() !== "" && Number(pp) !== 0;
      if (hasParent) return !!this.groupsById[Number(pp)];
      const owner = g.owner_unique_ID != null && String(g.owner_unique_ID).trim() !== "" ? String(g.owner_unique_ID).trim() : "";
      if (owner) return true;
      return !!this.parentOfSelected;
    },
    groupTreeNodes() {
      const byParent = {};
      const nodesById = {};
      const groups = this.groupsAll || [];

      groups.forEach((g) => {
        const gid = Number(g.id);
        const shouldInjectMembers = this.shouldInjectGroupMembers(gid);
        const node = {
          key: String(g.id),
          label: g.name,
          data: { type: "group", group: g },
          leaf: shouldInjectMembers ? false : undefined,
          children: [],
        };
        nodesById[String(g.id)] = node;
        const parentKey =
          g.parent_group_id === null || g.parent_group_id === "" || Number(g.parent_group_id) === 0
            ? "__root__"
            : String(g.parent_group_id);
        if (!byParent[parentKey]) byParent[parentKey] = [];
        byParent[parentKey].push(node);
      });

      Object.keys(nodesById).forEach((id) => {
        const children = byParent[id] || [];
        children.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
        nodesById[id].children = children;
      });

      const roots = (byParent["__root__"] || []).sort((a, b) => (a.label || "").localeCompare(b.label || ""));

      const injectMembers = (nodes) => {
        for (const n of nodes) {
          const groupId = Number(n.key);
          if (this.shouldInjectGroupMembers(groupId)) {
            const orgKids = [...(n.children || [])];
            const memberRows =
              groupId === this.patientsGroupId
                ? this.patientsMembers || []
                : this.loadedGlobalGroupMembers[String(groupId)] || [];
            const sortedMemberRows = [...memberRows].sort((a, b) => this.compareUsersByLastFirst(a, b));
            const memberKids = sortedMemberRows.map((m) => {
              const uid = String(m.unique_ID);
              if (groupId !== this.patientsGroupId) {
                return {
                  key: `member:${groupId}:${uid}`,
                  label: this.formatMemberName(m) || uid,
                  data: { type: "member", unique_ID: uid, member: m, group_id: groupId },
                  leaf: false,
                  children: this.buildOwnerGroupTreeNodes(this.loadedOwnerGroups[uid] || []),
                };
              }
              return {
                key: `patient:${uid}`,
                label: this.formatMemberName(m) || uid,
                data: { type: "patient", unique_ID: uid, member: m },
                leaf: false,
                children: this.buildOwnerGroupTreeNodes(this.loadedOwnerGroups[uid] || []),
              };
            });
            n.children = [...orgKids, ...memberKids];
            injectMembers(orgKids);
          } else if (n.children && n.children.length) {
            injectMembers(n.children);
          }
        }
      };
      injectMembers(roots);
      return roots;
    },
    /** Grant-status radio filter only (original filteredPrivileges logic). */
    visibilityFilteredPrivileges() {
      const list = this.privilegesAll || [];
      if (this.privilegeVisibilityFilter === "granted") {
        return list.filter((p) => p.effective_granted === true);
      }
      if (this.privilegeVisibilityFilter === "not_granted") {
        return list.filter((p) => p.effective_granted === false);
      }
      if (this.privilegeVisibilityFilter === "denied") {
        if (this.isUserSubject) {
          return list.filter((p) => p.user_override === "deny");
        }
        return [];
      }
      return list;
    },
    /** Grant filter first, then optional panel-2 search — both stack on the same list. */
    filteredPrivileges() {
      const visible = this.visibilityFilteredPrivileges;
      const q = String(this.privilegeSearchQuery || "").trim().toLowerCase();
      if (!q) return visible;
      return visible.filter((p) => this.privilegeMatchesSearch(p, q));
    },
    privilegeTreeNodes() {
      const byCategory = {};
      for (const p of this.filteredPrivileges) {
        const category = String(p.category || "Uncategorized").trim() || "Uncategorized";
        if (!byCategory[category]) byCategory[category] = [];
        byCategory[category].push(p);
      }
      const categories = Object.keys(byCategory).sort((a, b) => a.localeCompare(b));
      return categories.map((cat) => {
        const items = byCategory[cat]
          .slice()
          .sort((a, b) =>
            String(a.name || a.label || "").localeCompare(String(b.name || b.label || ""))
          )
          .map((p) => ({
            key: `priv:${p.code ?? p.id}`,
            label: this.formatPrivilegeTreeLabel(p),
            data: { type: "privilege", privilege: p },
            leaf: true,
          }));
        return {
          key: `cat:${cat}`,
          label: cat,
          data: { type: "category", category: cat },
          children: items,
        };
      });
    },
    privilegeGrantDirty() {
      return this.privilegeGrantChoice !== this.privilegeGrantBaseline;
    },
  },
  watch: {
    subjectPrivilegeKey() {
      this.loadPrivilegeAssignments();
    },
    isUserSubject(val) {
      if (!val && this.privilegeVisibilityFilter === "denied") {
        this.privilegeVisibilityFilter = "all";
      }
    },
    /** Re-expand matching categories after each keystroke (tree rebuilds on the next tick). */
    privilegeSearchQuery() {
      this.$nextTick(() => this.expandPrivilegeCategoriesForSearch());
    },
  },
  methods: {
    /** Privilege name, with grant status suffix in self read-only view. */
    formatPrivilegeTreeLabel(p) {
      const name = String(p.name ?? p.label ?? "");
      if (!this.isSelfViewOnly) return name;
      if (p.user_override === "deny") return `${name} — denied`;
      if (p.effective_granted === true) return `${name} — granted`;
      return `${name} — not granted`;
    },
    /** True when the privilege row matches the panel-2 search query (case-insensitive substring). */
    privilegeMatchesSearch(p, q) {
      const hay = [p.name, p.label, p.category, p.code, p.description]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    },
    /** Open every category that still has matches so search results stay visible in the tree. */
    expandPrivilegeCategoriesForSearch() {
      const q = String(this.privilegeSearchQuery || "").trim();
      if (!q) return;
      const expanded = { ...(this.expandedPrivilegeKeys || {}) };
      for (const cat of this.privilegeTreeNodes) {
        if (cat.children && cat.children.length) {
          expanded[cat.key] = true;
        }
      }
      this.expandedPrivilegeKeys = expanded;
    },
    cloneCatalogToPrivilegesAll() {
      this.privilegesAll = (this.privilegesCatalog || []).map((p) => ({ ...p }));
    },
    async loadPrivilegeAssignments() {
      if (!this.accountNumber) return;
      if (!this.hasSubjectSelection) {
        this.cloneCatalogToPrivilegesAll();
        return;
      }
      this.loadingPrivilegeAssignments = true;
      try {
        const content = {
          Privileges: {
            API_Meth: "GetPrivilegeAssignmentsForSubject",
            accountnumber: this.accountNumber,
            subject_type: this.isUserSubject ? "user" : "group",
            ...(this.selectedGroupId && !this.isUserSubject ? { group_id: this.selectedGroupId } : {}),
            ...(this.isUserSubject && this.selectedPatientUniqueId ? { unique_ID: this.selectedPatientUniqueId } : {}),
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        const ok = payload && String(payload.status || "").toLowerCase().includes("success");
        if (ok && Array.isArray(list)) {
          this.privilegesAll = list.map((row) => ({ ...row }));
        } else {
          this.cloneCatalogToPrivilegesAll();
        }
      } catch (e) {
        this.cloneCatalogToPrivilegesAll();
      } finally {
        if (this.selectedPrivilege) {
          const code = this.privilegeCodeOf(this.selectedPrivilege);
          const next = (this.privilegesAll || []).find((p) => this.privilegeCodeOf(p) === code);
          if (next) {
            this.selectedPrivilege = next;
            this.syncPrivilegeGrantFromRow(next);
            this.selectedPrivilegeKeys = { [`priv:${code}`]: true };
          }
        }
        this.loadingPrivilegeAssignments = false;
      }
    },
    clearPrivilegeSelection() {
      this.selectedPrivilege = null;
      this.selectedPrivilegeKeys = {};
    },
    onPrivilegeNodeSelect(node) {
      if (!node || !node.data || node.data.type !== "privilege" || !node.data.privilege) {
        return;
      }
      this.selectedPrivilege = node.data.privilege;
      this.syncPrivilegeGrantFromRow(node.data.privilege);
    },
    onPrivilegeRowClick(node) {
      const key = node?.key != null ? String(node.key) : "";
      if (!key) return;
      if (node?.data?.type === "privilege") {
        this.onPrivilegeNodeSelect(node);
        this.selectedPrivilegeKeys = { [key]: true };
        return;
      }
      // Category accordion: whole-row click toggles open/closed.
      const expanded = { ...(this.expandedPrivilegeKeys || {}) };
      if (expanded[key]) delete expanded[key];
      else expanded[key] = true;
      this.expandedPrivilegeKeys = expanded;
    },
    syncPrivilegeGrantFromRow(p) {
      // Default to not granted if no privilege is selected.
      if (!p) {
        this.privilegeGrantChoice = "not_granted";
        this.privilegeGrantBaseline = "not_granted";
        return;
      }
      if (this.isUserSubject) {
        const o = p.user_override;
        // Radios reflect *effective* access for the user (same as the middle-panel filters).
        // Deny always wins; otherwise Grant here if explicitly granted OR inherited from a group.
        if (o === "deny") {
          this.privilegeGrantChoice = "deny";
        } else if (o === "grant" || p.effective_granted === true) {
          this.privilegeGrantChoice = "grant";
        } else {
          this.privilegeGrantChoice = "not_granted";
        }
      } else {
        this.privilegeGrantChoice = p.group_granted ? "granted" : "not_granted";
      }
      this.privilegeGrantBaseline = this.privilegeGrantChoice;
    },
    /**
     * A permission is addressed by its `code`, never by a number.
     *
     * `PermissionResource` deliberately emits no `id` — legacy numeric
     * privilege ids were hard-coded into this SPA and had already collided
     * across environments — and the API binds `{privilege}` on `code`. The
     * privileges store keeps an `id` key for the shape's sake, but it holds the
     * same string. Coercing either with `Number()` yields `NaN`, which is what
     * silently disabled Save on this page.
     */
    privilegeCodeOf(privilege) {
      const code = privilege && (privilege.code ?? privilege.id);

      return typeof code === "string" && code !== "" ? code : null;
    },
    async savePrivilegeGrant() {
      if (this.isSelfViewOnly) return;
      if (!this.selectedPrivilege || !this.accountNumber) return;
      if (!this.privilegeGrantDirty) return;
      const code = this.privilegeCodeOf(this.selectedPrivilege);
      if (!code) {
        window.alert("This privilege cannot be saved: it arrived without a code.");
        return;
      }
      this.savingPrivilegeGrant = true;
      try {
        const body = {
          Privileges: {
            API_Meth: "SavePrivilegeGrant",
            accountnumber: this.accountNumber,
            privilege_code: code,
            subject_type: this.isUserSubject ? "user" : "group",
          },
        };
        if (this.isUserSubject) {
          body.Privileges.unique_ID = this.selectedPatientUniqueId;
          const c = this.privilegeGrantChoice;
          body.Privileges.user_mode =
            c === "grant" ? "grant" : c === "deny" ? "deny" : "inherit";
        } else {
          body.Privileges.group_id = this.selectedGroupId;
          body.Privileges.granted = this.privilegeGrantChoice === "granted";
        }
        const res = await axios.post(CURR_API + "/tswebhook.php", body, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const ok = payload && String(payload.status || "").toLowerCase().includes("success");
        if (!ok) {
          window.alert("Privilege settings were not saved.");
          return;
        }
        await this.loadPrivilegeAssignments();
        syncPrivilegesAfterSessionChange();
        const next =
          (this.privilegesAll || []).find((p) => this.privilegeCodeOf(p) === code) || null;
        this.selectedPrivilege = next;
        if (this.selectedPrivilege) {
          this.syncPrivilegeGrantFromRow(this.selectedPrivilege);
          this.selectedPrivilegeKeys = { [`priv:${code}`]: true };
        }
        window.alert("Privilege settings were saved.");
      } catch (e) {
        // The bridge now rejects an unresolvable assignment rather than
        // fabricating a success, so a swallowed error here would put the page
        // straight back to reporting a save that never happened.
        window.alert("Privilege settings were not saved. Please try again.");
      } finally {
        this.savingPrivilegeGrant = false;
      }
    },
    quitPrivilegePanel() {
      this.clearPrivilegeSelection();
    },
    currentSelectedTreeKey() {
      const keys = this.selectedTreeKeys || {};
      return Object.keys(keys).find((k) => !!keys[k]) || null;
    },
    findTreeNodeByKey(nodes, targetKey) {
      const list = Array.isArray(nodes) ? nodes : [];
      for (const n of list) {
        if (String(n.key) === String(targetKey)) return n;
        const hit = this.findTreeNodeByKey(n.children || [], targetKey);
        if (hit) return hit;
      }
      return null;
    },
    collectSubtreeKeys(node, out = new Set()) {
      if (!node) return out;
      out.add(String(node.key));
      const kids = Array.isArray(node.children) ? node.children : [];
      for (const c of kids) this.collectSubtreeKeys(c, out);
      return out;
    },
    subtreeKeysForKey(rootKey) {
      if (!rootKey) return new Set();
      const node = this.findTreeNodeByKey(this.groupTreeNodes || [], rootKey);
      if (!node) return new Set([String(rootKey)]);
      return this.collectSubtreeKeys(node, new Set());
    },
    collapseDeselectedBranch(prevKey, nextKey) {
      if (!prevKey || !nextKey || String(prevKey) === String(nextKey)) return;
      const prevSubtree = this.subtreeKeysForKey(prevKey);
      const nextSubtree = this.subtreeKeysForKey(nextKey);
      const sameBranch = prevSubtree.has(String(nextKey)) || nextSubtree.has(String(prevKey));
      if (sameBranch) return;
      const nextExpanded = { ...(this.expandedTreeKeys || {}) };
      for (const k of prevSubtree) delete nextExpanded[String(k)];
      this.expandedTreeKeys = nextExpanded;
    },
    normalizeApiPayload(data) {
      if (!data || typeof data !== "object") return {};
      if (Object.prototype.hasOwnProperty.call(data, "status") || Object.prototype.hasOwnProperty.call(data, "data")) {
        return data;
      }
      const keys = Object.keys(data);
      if (keys.length === 1 && data[keys[0]] && typeof data[keys[0]] === "object") {
        return data[keys[0]];
      }
      return data;
    },
    formatMemberName(m) {
      const first = m.user_Fname || "";
      const last = m.user_Lname || "";
      return `${first} ${last}`.trim() || m.username || m.unique_ID;
    },
    userNameParts(user) {
      const firstExplicit = String(user?.first_name || user?.user_Fname || user?.firstname || "").trim();
      const lastExplicit = String(user?.last_name || user?.user_Lname || user?.lastname || "").trim();
      if (firstExplicit || lastExplicit) {
        return {
          last: lastExplicit.toLowerCase(),
          first: firstExplicit.toLowerCase(),
        };
      }
      const name = String(user?.name || user?.label || "").trim();
      if (name) {
        const parts = name.split(/\s+/).filter(Boolean);
        if (parts.length === 1) return { last: parts[0].toLowerCase(), first: "" };
        const first = parts[0];
        const last = parts.slice(1).join(" ");
        return { last: last.toLowerCase(), first: first.toLowerCase() };
      }
      const fallback = String(user?.username || user?.email || user?.unique_ID || "").toLowerCase();
      return { last: fallback, first: "" };
    },
    compareUsersByLastFirst(a, b) {
      const aParts = this.userNameParts(a);
      const bParts = this.userNameParts(b);
      const lastCmp = aParts.last.localeCompare(bParts.last);
      if (lastCmp !== 0) return lastCmp;
      const firstCmp = aParts.first.localeCompare(bParts.first);
      if (firstCmp !== 0) return firstCmp;
      const aFallback = String(a?.name || a?.label || a?.username || a?.email || a?.unique_ID || "").toLowerCase();
      const bFallback = String(b?.name || b?.label || b?.username || b?.email || b?.unique_ID || "").toLowerCase();
      return aFallback.localeCompare(bFallback);
    },
    isTreePersonNode(node) {
      return node?.data?.type === "patient" || node?.data?.type === "member";
    },
    isTreeGroupNode(node) {
      return !this.isTreePersonNode(node);
    },
    normalizeAccountType(value) {
      return String(value || "").trim().toLowerCase();
    },
    ownerRootGroupLabel(uid) {
      const id = String(uid || "").trim();
      if (!id) return "Users";
      const au = (this.accountUsers || []).find((u) => String(u.unique_ID) === id);
      const t = this.normalizeAccountType(au?.account_type);
      if (t === "patient") return "Patients";
      if (t === "provider") return "Providers";
      if (["nurse", "registered nurse", "licensed practical nurse"].includes(t)) return "Nurses";
      if (["home health aid", "home health aids", "home health aide", "home health aides"].includes(t)) {
        return "Home Health Aids";
      }
      if (["allied professional", "allied professionals"].includes(t)) return "Allied Professionals";
      // GCM account_type → root group label on Privileges tree.
      if (["geriatric care manager", "geriatric care managers"].includes(t)) {
        return "Geriatric Care Managers";
      }
      if ((this.patientsMembers || []).some((x) => String(x.unique_ID) === id)) return "Patients";
      for (const [gid, label] of [
        [this.nursesGroupId, "Nurses"],
        [this.providersGroupId, "Providers"],
        [this.homeHealthAidsGroupId, "Home Health Aids"],
        [this.alliedProfessionalsGroupId, "Allied Professionals"],
        [this.geriatricCareManagersGroupId, "Geriatric Care Managers"],
      ]) {
        if (!gid) continue;
        const rows = (this.loadedGlobalGroupMembers || {})[String(gid)] || [];
        if (rows.some((x) => String(x.unique_ID) === id)) return label;
      }
      return "Users";
    },
    globalGroupIdForUid(uid) {
      const id = String(uid || "").trim();
      if (!id) return null;
      if (this.patientsGroupId && (this.patientsMembers || []).some((x) => String(x.unique_ID) === id)) {
        return this.patientsGroupId;
      }
      for (const gid of [
        this.nursesGroupId,
        this.providersGroupId,
        this.homeHealthAidsGroupId,
        this.alliedProfessionalsGroupId,
        this.geriatricCareManagersGroupId,
      ]) {
        if (!gid) continue;
        const rows = (this.loadedGlobalGroupMembers || {})[String(gid)] || [];
        if (rows.some((x) => String(x.unique_ID) === id)) return gid;
      }
      const au = (this.accountUsers || []).find((u) => String(u.unique_ID) === id);
      if (au) {
        const t = this.normalizeAccountType(au.account_type);
        if (t === "patient") return this.patientsGroupId;
        if (t === "provider") return this.providersGroupId;
        if (["nurse", "registered nurse", "licensed practical nurse"].includes(t)) return this.nursesGroupId;
        if (["home health aid", "home health aids", "home health aide", "home health aides"].includes(t)) {
          return this.homeHealthAidsGroupId;
        }
        if (["allied professional", "allied professionals"].includes(t)) {
          return this.alliedProfessionalsGroupId;
        }
        // Resolve GCM users back to the org-level GCM group for tree expansion.
        if (["geriatric care manager", "geriatric care managers"].includes(t)) {
          return this.geriatricCareManagersGroupId;
        }
      }
      return null;
    },
    applyOwnerPersonExpansion(expanded, ownerUid) {
      const o = String(ownerUid || "").trim();
      if (!o) return;
      let ggid = this.globalGroupIdForUid(o);
      if (ggid == null) {
        const label = this.ownerRootGroupLabel(o);
        if (label === "Patients") ggid = this.patientsGroupId;
        else if (label === "Nurses") ggid = this.nursesGroupId;
        else if (label === "Providers") ggid = this.providersGroupId;
        else if (label === "Home Health Aids") ggid = this.homeHealthAidsGroupId;
        else if (label === "Allied Professionals") ggid = this.alliedProfessionalsGroupId;
        // Fall back to GCM org group when expanding a GCM owner's subgroups.
        else if (label === "Geriatric Care Managers") ggid = this.geriatricCareManagersGroupId;
      }
      if (ggid == null || !Number(ggid)) return;
      const isPatientBranch = this.patientsGroupId != null && Number(ggid) === Number(this.patientsGroupId);
      if (isPatientBranch) {
        expanded[String(this.patientsGroupId)] = true;
        expanded[`patient:${o}`] = true;
      } else {
        expanded[String(ggid)] = true;
        expanded[`member:${ggid}:${o}`] = true;
      }
    },
    /** Role globals + any group with a parent (custom subgroups) get members as tree children. */
    shouldInjectGroupMembers(groupId) {
      const gid = Number(groupId);
      if (!Number.isFinite(gid) || gid <= 0) return false;
      if (this.memberInjectableGroupIds.has(gid)) return true;
      const g = this.groupsById[gid] || this.groupsById[String(gid)];
      if (!g) return false;
      const parent = g.parent_group_id;
      return parent != null && String(parent).trim() !== "" && Number(parent) !== 0;
    },
    buildOwnerGroupTreeNodes(ownedList) {
      const list = Array.isArray(ownedList) ? ownedList : [];
      if (!list.length) return [];
      const byParent = {};
      const nodesById = {};
      list.forEach((g) => {
        const node = { key: String(g.id), label: g.name, data: { type: "group", group: g }, children: [] };
        nodesById[String(g.id)] = node;
        const parentKey =
          g.parent_group_id === null || g.parent_group_id === "" || Number(g.parent_group_id) === 0
            ? "__root__"
            : String(g.parent_group_id);
        if (!byParent[parentKey]) byParent[parentKey] = [];
        byParent[parentKey].push(node);
      });
      Object.keys(nodesById).forEach((id) => {
        const children = byParent[id] || [];
        children.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
        nodesById[id].children = children;
      });
      return (byParent["__root__"] || []).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    },
    async loadAccountUsers() {
      if (!this.accountNumber) return;
      try {
        const content = { Account: { API_Meth: "GetAccountUsers", accountnumber: this.accountNumber } };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        this.accountUsers = Array.isArray(list)
          ? list
              .map((u) => ({
                unique_ID: u.subaccountnumber || u.unique_ID || u.subAccountNumber || u.id,
                name: `${u.firstname || u.Firstname || u.user_Fname || ""} ${u.lastname || u.Lastname || u.user_Lname || ""}`.trim(),
                email: u.email || u.user_email || "",
                username: u.username || "",
                account_type: u.account_type || u.accountType || "",
                first_name: u.firstname || u.Firstname || u.user_Fname || "",
                last_name: u.lastname || u.Lastname || u.user_Lname || "",
              }))
              .sort((a, b) => this.compareUsersByLastFirst(a, b))
          : [];
      } catch (e) {
        this.accountUsers = [];
      }
    },
    async loadPatientsMembersForTree() {
      this.patientsMembers = [];
      if (!this.accountNumber || !this.patientsGroupId) return;
      try {
        const content = {
          Groups: { API_Meth: "GetGroupMembers", accountnumber: this.accountNumber, group_id: this.patientsGroupId },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        this.patientsMembers = Array.isArray(list) ? list : [];
      } catch (e) {
        this.patientsMembers = [];
      }
    },
    async loadGlobalGroupMembersForTree(groupId) {
      const gid = Number(groupId);
      if (!this.accountNumber || !gid) return;
      try {
        const content = { Groups: { API_Meth: "GetGroupMembers", accountnumber: this.accountNumber, group_id: gid } };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        this.loadedGlobalGroupMembers = { ...this.loadedGlobalGroupMembers, [String(gid)]: Array.isArray(list) ? list : [] };
      } catch (e) {
        this.loadedGlobalGroupMembers = { ...this.loadedGlobalGroupMembers, [String(gid)]: [] };
      }
    },
    async fetchOwnerGroups(ownerUniqueId) {
      const uid = ownerUniqueId != null ? String(ownerUniqueId).trim() : "";
      if (!this.accountNumber || !uid) return;
      try {
        const content = { Groups: { API_Meth: "GetGroupsByOwner", accountnumber: this.accountNumber, owner_unique_ID: uid } };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        this.loadedOwnerGroups = { ...this.loadedOwnerGroups, [uid]: Array.isArray(list) ? list : [] };
      } catch (e) {
        this.loadedOwnerGroups = { ...this.loadedOwnerGroups, [uid]: [] };
      }
    },
    async loadGroups() {
      if (!this.accountNumber) return;
      this.loadingGroups = true;
      try {
        const content = { Groups: { API_Meth: "GetGroupsByAccount", accountnumber: this.accountNumber } };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        this.groupsAll = Array.isArray(list) ? list : [];
        this.loadedGlobalGroupMembers = {};
        this.loadedOwnerGroups = {};
        await this.loadAccountUsers();
        await this.loadPatientsMembersForTree();
      } finally {
        this.loadingGroups = false;
      }
    },
    async loadPrivileges() {
      if (!this.accountNumber) return;
      this.loadingPrivileges = true;
      try {
        const content = {
          Privileges: {
            API_Meth: "GetPrivilegesByAccount",
            accountnumber: this.accountNumber,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        const arr = Array.isArray(list) ? list : [];
        this.privilegesCatalog = arr.map((p) => ({ ...p }));
        this.cloneCatalogToPrivilegesAll();
      } catch (e) {
        this.privilegesCatalog = [];
        this.privilegesAll = [];
      } finally {
        this.loadingPrivileges = false;
      }
    },
    selectGroup(groupId) {
      if (this.isSelfViewOnly) return;
      if (!groupId) return;
      if (this.layoutMode !== "privileges_first") {
        this.clearPrivilegeSelection();
      }
      this.selectedPatientUniqueId = null;
      this.selectedPersonGlobalGroupId = null;
      this.selectedGroupId = Number(groupId);
      this.syncTreeStateFromSelection();
    },
    selectPatient(uniqueId, globalParentGroupId) {
      if (this.isSelfViewOnly) return;
      const uid = uniqueId != null ? String(uniqueId).trim() : "";
      if (!uid) return;
      if (this.layoutMode !== "privileges_first") {
        this.clearPrivilegeSelection();
      }
      this.selectedPatientUniqueId = uid;
      this.selectedGroupId = null;
      if (globalParentGroupId !== undefined && globalParentGroupId !== null && globalParentGroupId !== "") {
        this.selectedPersonGlobalGroupId = Number(globalParentGroupId);
      } else {
        this.selectedPersonGlobalGroupId = this.globalGroupIdForUid(uid);
      }
      this.syncTreeStateFromSelection();
    },
    async activateTreeNode(node, ensureExpanded = false) {
      if (this.isSelfViewOnly) return;
      if (!node || !node.key) return;
      const key = String(node.key);
      const prevSelectedKey = this.currentSelectedTreeKey();
      this.collapseDeselectedBranch(prevSelectedKey, key);
      if (ensureExpanded) {
        this.expandedTreeKeys = { ...(this.expandedTreeKeys || {}), [key]: true };
      }
      if (key.startsWith("patient:")) {
        const uid = key.split(":")[1];
        this.selectPatient(uid, this.patientsGroupId);
        await this.fetchOwnerGroups(uid);
        return;
      }
      if (key.startsWith("member:")) {
        const parts = key.split(":");
        const gid = Number(parts[1] || 0);
        const uid = parts[2] || "";
        if (uid) {
          this.selectPatient(uid, gid);
          await this.fetchOwnerGroups(uid);
        }
        if (gid && this.shouldInjectGroupMembers(gid) && gid !== this.patientsGroupId) {
          await this.loadGlobalGroupMembersForTree(gid);
        }
        return;
      }
      const gid = Number(key);
      if (!gid) return;
      this.selectGroup(gid);
      const g = this.groupsById[gid];
      const owner = g && g.owner_unique_ID != null && String(g.owner_unique_ID).trim() !== "" ? String(g.owner_unique_ID).trim() : "";
      if (owner) {
        await this.fetchOwnerGroups(owner);
      }
      if (this.shouldInjectGroupMembers(gid) && gid !== this.patientsGroupId) {
        await this.loadGlobalGroupMembersForTree(gid);
      }
    },
    async onTreeNodeSelect(node) {
      // Do not force-expand here; row click owns open/close toggling.
      await this.activateTreeNode(node, false);
    },
    async onTreeRowClick(node) {
      const key = node?.key != null ? String(node.key) : "";
      if (!key) return;
      const shouldExpand = !this.expandedTreeKeys?.[key];
      await this.activateTreeNode(node, false);
      await this.$nextTick();
      const expanded = { ...(this.expandedTreeKeys || {}) };
      if (shouldExpand) {
        expanded[key] = true;
      } else {
        delete expanded[key];
      }
      this.expandedTreeKeys = expanded;
    },
    async onTreeNodeExpand(node) {
      // Node is already expanded by PrimeVue; load data without forcing expand again.
      await this.activateTreeNode(node, false);
    },
    syncTreeStateFromSelection() {
      if (this.selectedPatientUniqueId && !this.selectedGroupId) {
        const uid = this.selectedPatientUniqueId;
        const ggid = this.selectedPersonGlobalGroupId;
        const expanded = { ...(this.expandedTreeKeys || {}) };
        const isStaffGlobal =
          ggid != null &&
          Number.isFinite(Number(ggid)) &&
          Number(ggid) > 0 &&
          Number(ggid) !== Number(this.patientsGroupId);
        if (isStaffGlobal) {
          this.selectedTreeKeys = { [`member:${ggid}:${uid}`]: true };
          // Keep parent role group visible; do not force the person row open.
          expanded[String(ggid)] = true;
        } else {
          this.selectedTreeKeys = { [`patient:${uid}`]: true };
          if (this.patientsGroupId) expanded[String(this.patientsGroupId)] = true;
        }
        this.expandedTreeKeys = expanded;
        return;
      }
      if (!this.selectedGroupId) {
        this.selectedTreeKeys = {};
        return;
      }
      const selectedId = Number(this.selectedGroupId);
      const expanded = { ...(this.expandedTreeKeys || {}) };
      this.selectedTreeKeys = { [String(selectedId)]: true };
      // Expand ancestors only so row-click can still collapse the selected node.
      const selectedGroup = this.groupsById[selectedId];
      let currentId = selectedGroup ? Number(selectedGroup.parent_group_id || 0) : 0;
      const visited = new Set();
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        expanded[String(currentId)] = true;
        const group = this.groupsById[currentId];
        if (!group) break;
        const parent = Number(group.parent_group_id || 0);
        const o = group.owner_unique_ID != null && String(group.owner_unique_ID).trim() !== "" ? String(group.owner_unique_ID).trim() : "";
        if (o) this.applyOwnerPersonExpansion(expanded, o);
        if (!parent || parent === currentId) break;
        currentId = parent;
      }
      this.expandedTreeKeys = expanded;
    },
    /**
     * Lock the page onto the logged-in user: no group tree, assignments only
     * for self, grant UI disabled by isSelfViewOnly.
     */
    async enterSelfViewOnly() {
      const uid = this.selfUniqueId;
      // Naming the subject fires the `subjectPrivilegeKey` watcher, which
      // loads it; loading here as well asked twice, and on the port each read
      // of a person's privileges is an audited request. Load here only when
      // the subject was already this person and so the watcher will not fire.
      const watcherLoads = String(this.selectedPatientUniqueId ?? "") !== String(uid || "")
        || this.selectedGroupId !== null;
      this.selectedGroupId = null;
      this.selectedPersonGlobalGroupId = null;
      this.selectedPatientUniqueId = uid || null;
      this.layoutMode = "privileges_first";
      // Self view: start on "only granted" so the list is useful without scrolling past denials.
      this.privilegeVisibilityFilter = "granted";
      this.clearPrivilegeSelection();
      if (!uid) {
        this.privilegesAll = [];
        return;
      }
      if (!watcherLoads) {
        await this.loadPrivilegeAssignments();
      }
    },
    async goUpLevel() {
      if (this.isSelfViewOnly) return;
      if (this.selectedPatientUniqueId && !this.selectedGroupId) {
        const ggid =
          this.selectedPersonGlobalGroupId != null && Number(this.selectedPersonGlobalGroupId) > 0
            ? Number(this.selectedPersonGlobalGroupId)
            : this.globalGroupIdForUid(this.selectedPatientUniqueId);
        if (ggid == null || !Number(ggid)) return;
        this.selectGroup(ggid);
        return;
      }

      const g = this.selectedGroup;
      if (!g || !this.selectedGroupId) return;

      const pp = g.parent_group_id;
      const hasParent = pp != null && String(pp).trim() !== "" && Number(pp) !== 0;
      if (hasParent) {
        const pid = Number(pp);
        if (this.groupsById[pid]) this.selectGroup(pid);
        return;
      }

      const owner = g.owner_unique_ID != null && String(g.owner_unique_ID).trim() !== "" ? String(g.owner_unique_ID).trim() : "";
      if (owner) {
        const roleGid = this.globalGroupIdForUid(owner);
        this.selectPatient(owner, roleGid != null ? roleGid : undefined);
        await this.fetchOwnerGroups(owner);
        return;
      }

      if (this.parentOfSelected) this.selectGroup(Number(this.parentOfSelected.id));
    },
  },
  async mounted() {
    // Permissions and the signed-in person are one `GET /me`; wait for it,
    // so editor vs read-only and the self view's subject are decided on the
    // session rather than before it has loaded.
    const privStore = usePrivilegesStore();
    if (!privStore.loadedKey) {
      await privStore.fetchForCurrentSession();
    }
    if (this.accountNumber) {
      useOrganizationStore().ensureLoaded(this.accountNumber);
    }
    if (this.isSelfViewOnly) {
      await this.enterSelfViewOnly();
      return;
    }
    this.loadGroups();
    this.loadPrivileges();
  },
};
</script>

<style scoped lang="scss">
/* Colors/fonts live in the stylebook (.eh-ds in brand-ds.scss). Layout stays here. */
.groups-page {
  padding: 1.5rem 0;
}

.groups-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0 0.25rem 1rem;
}

.groups-header-top {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.groups-header-top h1 {
  margin: 0;
}

.self-view-banner {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.4;
  opacity: 0.85;
}

.groups-body {
  margin-top: 0.5rem;
}

.privileges-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: stretch;
}

.privileges-layout-toggle {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem 1.25rem;
  padding: 0 0.15rem;
}

.privileges-layout-toggle-option {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
}

.privileges-main-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 0.75rem;
  align-items: stretch;
  min-height: 0;
}

.privileges-main-row--self .tree-panel-slot {
  flex: 1 1 18rem;
  min-width: 0;
}

.tree-panel-slot {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.tree-panel-slot--access {
  flex: 1 1 0;
  min-width: 0;
}

.groups-panel--placeholder {
  min-height: 320px;
}

.groups-panel {
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  padding: 0.75rem 0.85rem;
  min-height: 320px;
  max-height: min(72vh, 640px);
}

.groups-panel--tree {
  min-height: 0;
}

.groups-panel--tree.groups-panel--placeholder {
  min-height: 320px;
}

.groups-panel--tree .groups-tree {
  flex: 1;
  min-height: 0;
}

.tree-panel-row--actions {
  display: flex;
  align-items: center;
  min-height: 2.75rem;
  margin-bottom: 0.5rem;
  flex-shrink: 0;
}

.tree-panel-row--actions .privileges-filter-row {
  margin-bottom: 0;
  width: 100%;
}

.tree-panel-row--actions-stack {
  flex-direction: column;
  align-items: stretch;
  gap: 0.55rem;
  min-height: auto;
}

.privileges-search-field input {
  width: 100%;
  padding: 0.55rem 0.8rem;
  border-radius: 10px;
  font-size: 0.95rem;
  box-sizing: border-box;
}

.tree-panel-row--title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
  min-height: 3.5rem;
  margin-bottom: 0.75rem;
  flex-shrink: 0;
}

.tree-panel-row--title .list-title {
  margin-bottom: 0;
}

.tree-panel-row--title .privilege-select-prompt-row {
  margin-bottom: 0;
}

.tree-panel-row--placeholder {
  visibility: hidden;
  pointer-events: none;
  min-height: 2.75rem;
}

.tree-panel-row--placeholder.tree-panel-row--title {
  min-height: 3.5rem;
}

.selection-prompt {
  margin-left: auto;
  text-align: right;
  font-size: 1rem;
}

.privilege-select-prompt-row {
  margin-left: auto;
}

.privilege-select-prompt {
  display: block;
  width: 100%;
  text-align: right;
  font-size: 1rem;
}

.list-title {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.privileges-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
  font-size: 0.9rem;
}

.privileges-filter-row label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.groups-tree {
  border-radius: 12px;
  padding: 0.3rem 0.2rem;
  min-height: 160px;
  overflow-y: auto;
}

.privilege-detail-panel {
  min-height: 0;
}

.privilege-detail-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.privilege-detail-placeholder {
  font-size: 0.88rem;
  margin: 0;
  line-height: 1.45;
}

.privilege-detail-name {
  font-size: 1.5rem;
  word-break: break-word;
  align-self: center;
}

.privilege-readonly-status {
  display: flex;
  justify-content: center;
  margin-top: 0.75rem;
}

.privilege-readonly-pill {
  display: inline-block;
  padding: 0.35rem 0.85rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 700;
  border: 1px solid;
}

.privilege-readonly-pill--granted {
  color: #1b5e20;
  border-color: #1b5e20;
  background: #e8f5e9;
}

.privilege-readonly-pill--not-granted {
  color: #5d4037;
  border-color: #a1887f;
  background: #efebe9;
}

.privilege-readonly-pill--denied {
  color: #b71c1c;
  border-color: #b71c1c;
  background: #ffebee;
}

.tree-node-label--granted .tree-node-label-text {
  color: #1b5e20;
}

.tree-node-label--not-granted .tree-node-label-text {
  opacity: 0.75;
}

.tree-node-label--denied .tree-node-label-text {
  color: #b71c1c;
}

.privilege-grant-radios {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.privilege-grant-option {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 3rem;
  cursor: pointer;
  user-select: none;
}

.privilege-inheritance-note {
  margin-top: 0.75rem;
  padding-top: 0.65rem;
  border-top: 1px solid;
  font-size: 1rem;
  line-height: 1.45;
}

.privilege-inheritance-note-lead {
  margin: 0 0 0.4rem;
  font-weight: 600;
}

.privilege-inheritance-note-list {
  margin: 0 0 0.5rem 1.1rem;
  padding: 0;
}

.privilege-inheritance-note-extra {
  margin: 0.35rem 0 0;
  font-weight: 500;
}

.privilege-inheritance-note-deny {
  margin: 0.5rem 0 0;
}

.privilege-detail-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid;
  flex-shrink: 0;
}

.privilege-detail-actions .btn {
  flex: 1;
  min-width: 5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.privilege-detail-actions .btn-primary:disabled {
  cursor: not-allowed;
  opacity: 1;
}

.tree-node-label {
  font-size: 0.93rem;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  align-self: stretch;
  width: 100%;
  min-width: 0;
  min-height: 2rem;
  padding: 0.15rem 0.35rem 0.15rem 0;
  box-sizing: border-box;
  cursor: pointer;
}

.tree-node-label-text {
  flex: 0 1 auto;
  min-width: 0;
}

.tree-node-label--group {
  font-weight: 600;
}

.tree-node-label--person {
  font-weight: 500;
  gap: 0.4rem;
}

.user-silhouette-icon {
  font-size: 0.9rem;
  line-height: 1;
}

:deep(.groups-tree .p-tree-node-content) {
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 8px;
}

:deep(.groups-tree .p-tree-node-label),
:deep(.groups-tree .p-treenode-label),
:deep(.groups-tree .p-tree-node-content > .p-tree-node-label) {
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  min-width: 0;
  width: 100%;
}

.btn {
  border: none;
  border-radius: 10px;
  padding: 0.48rem 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@media (max-width: 1100px) {
  .privileges-main-row {
    flex-direction: column;
    flex-wrap: wrap;
  }

  .tree-panel-slot,
  .tree-panel-slot--access {
    flex: 1 1 auto;
    width: 100%;
    max-width: none;
  }

  .groups-panel {
    max-height: none;
  }

  .groups-panel--tree .groups-tree {
    max-height: 42vh;
  }
}
</style>
